import { Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import type { WorkflowFormLookupBinding } from '@app/shared/workflow-commands/workflow-form-lookup-contract';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import { WorkflowLibrarySelectorComponent } from './workflow-library-selector.component';
import type { WorkflowLibraryClosedReason, WorkflowLibraryEntry, WorkflowLibraryFooterAction } from './workflow-library-selector.types';
import {
  isWorkflowLookupSourcePage, isWorkflowLookupSourceRead, workflowLookupSourceOwnerKey, workflowLookupSourceReason, workflowLookupSourceBindingKey,
  type WorkflowLookupSource, type WorkflowLookupSourceDependency, type WorkflowLookupSourceOwner,
  type WorkflowLookupSourceQuery, type WorkflowLookupSourceSelection,
} from './workflow-lookup-source-authoring.models';

/** Draft-bound discovery only. The Work owner validates and applies one explicit source conversion. */
@Component({
  selector: 'phms-workflow-capture-lookup-selector', standalone: true,
  imports: [CommonModule, WorkflowLibrarySelectorComponent],
  templateUrl: './workflow-capture-lookup-selector.component.html',
  styleUrl: './workflow-capture-lookup-selector.component.scss',
})
export class WorkflowCaptureLookupSelectorComponent implements OnChanges, DoCheck, OnDestroy {
  @Input() open = false;
  @Input() active = true;
  @Input() disabled = false;
  @Input() owner: () => WorkflowLookupSourceOwner | null = () => null;
  @Input() context: () => string = () => '';
  @Input() savedBinding: WorkflowFormLookupBinding | null = null;
  @Input() description = 'Choose lookup choices for this field. This replaces its inline choices and updates the local form format. Save draft to keep the change.';
  @Input() choiceReason: (source: WorkflowLookupSource) => string = () => '';
  @Input() dependency: (source: WorkflowLookupSource) => WorkflowLookupSourceDependency | null = () => null;
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<WorkflowLibraryClosedReason>();
  @Output() sourceChosen = new EventEmitter<WorkflowLookupSourceSelection>();
  @Output() dependencyRequested = new EventEmitter<string>();

  private readonly service = inject(PlatformWorkflowDefinitionLifecycleService);
  private request = 0;
  private subscription: Subscription | null = null;
  private savedRead: Subscription | null = null;
  private savedRequest = 0;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private baseline = '';
  private destroyed = false;
  private pendingSelection: WorkflowLookupSourceSelection | null = null;
  private pendingDependency: { id: string; baseline: string } | null = null;
  internalOpen = false;
  loading = false;
  error = '';
  retryDisabled = false;
  sources: WorkflowLookupSource[] = [];
  query: WorkflowLookupSourceQuery = { search: '', pageNumber: 1, pageSize: 25 };
  totalCount = 0;
  savedSource: WorkflowLookupSource | null = null;
  savedSourceLoading = false;
  savedSourceError = '';

  get liveContext(): string { return this.currentKey(); }
  get pageMessage(): string {
    if (this.loading) return 'Reading sources for the saved draft’s workflow context.';
    if (!this.totalCount) return 'No matching sources. Search uses configuration codes, domain codes and names.';
    const first = (this.query.pageNumber - 1) * this.query.pageSize + 1;
    return `Sources ${first}–${Math.min(first + this.sources.length - 1, this.totalCount)} of ${this.totalCount}. Choices follow the effective workflow context; this does not pin a configuration version.`;
  }
  get footerActions(): readonly WorkflowLibraryFooterAction[] {
    return [{ id: 'previous', label: 'Previous page', disabled: this.query.pageNumber <= 1 },
      { id: 'next', label: 'Next page', disabled: this.query.pageNumber * this.query.pageSize >= this.totalCount }];
  }
  get entries(): WorkflowLibraryEntry[] {
    return this.sources.map(source => {
      const dependency = this.dependency(source), reason = workflowLookupSourceReason(source) || this.choiceReason(source);
      const current = this.savedBinding && workflowLookupSourceBindingKey(this.savedBinding) === workflowLookupSourceBindingKey(source.binding);
      return { id: workflowLookupSourceBindingKey(source.binding), label: source.label, categoryId: 'lookup', icon: 'pi pi-list',
        description: `${current ? 'Current source. ' : ''}${source.binding.configurationCode} / ${source.binding.domainCode}${source.versionCode ? ` · ${source.versionCode}` : ''}. ${source.description || ''} ${source.choices.filter(choice => choice.isEnabled).length} enabled choices.`,
        disabledReason: reason, actionLabel: 'Use lookup choices',
        ...(dependency ? { ownerAction: { id: dependency.id, label: 'Review visibility' } } : {}),
      };
    });
  }
  source(id: string): WorkflowLookupSource | null {
    const rows = this.sources.filter(source => workflowLookupSourceBindingKey(source.binding) === id);
    return rows.length === 1 ? rows[0] : null;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open && this.active && !this.disabled) this.begin();
    else if (!this.open || !this.active || this.disabled) this.cancel();
  }
  ngDoCheck(): void {
    if (this.internalOpen && this.baseline !== this.currentKey()) this.cancel();
  }
  ngOnDestroy(): void { this.destroyed = true; this.cancel(); }
  libraryOpened(): void { this.opened.emit(); }
  libraryClosed(reason: WorkflowLibraryClosedReason): void {
    const selected = this.pendingSelection, dependency = this.pendingDependency, baseline = this.baseline;
    this.pendingSelection = null; this.pendingDependency = null; this.internalOpen = false; this.stopRequest(); this.stopSavedRead();
    // Release the owning focus suspension before a committed selection or owner handoff.
    this.closed.emit(reason);
    if (reason !== 'input' || !this.contextCurrent(baseline)) return;
    if (selected && workflowLookupSourceOwnerKey(this.owner()) === workflowLookupSourceOwnerKey(selected.owner)
      && this.context() === selected.context && !workflowLookupSourceReason(selected.source) && !this.choiceReason(selected.source)) this.sourceChosen.emit(selected);
    else if (dependency && dependency.baseline === baseline) this.dependencyRequested.emit(dependency.id);
  }
  search(value: string): void {
    if (this.retryDisabled || !this.current(this.baseline) || !this.internalOpen) return;
    this.stopRequest(); this.query = { ...this.query, search: value.slice(0, 120), pageNumber: 1 }; this.error = ''; this.sources = []; this.totalCount = 0;
    const baseline = this.baseline;
    this.debounce = setTimeout(() => { this.debounce = null; if (this.current(baseline) && this.internalOpen) this.load(); }, 250);
  }
  page(id: string): void {
    if (this.retryDisabled || !this.current(this.baseline) || !this.internalOpen || this.loading || !['previous', 'next'].includes(id)) return;
    const next = this.query.pageNumber + (id === 'previous' ? -1 : 1);
    if (next < 1 || next > Math.ceil(this.totalCount / this.query.pageSize)) return;
    this.query = { ...this.query, pageNumber: next }; this.load();
  }
  retry(): void { if (!this.retryDisabled && this.current(this.baseline) && this.internalOpen) { this.load(); this.stopSavedRead(); this.readSavedSource(); } }
  choose(id: string): void {
    const source = this.source(id), baseline = this.baseline;
    if (this.retryDisabled || !this.internalOpen || !this.current(baseline) || this.loading || !source || workflowLookupSourceReason(source) || this.choiceReason(source)) return;
    const owner = structuredClone(this.owner()!), context = this.context(), binding = structuredClone(source.binding);
    this.stopRequest(); const request = this.request; this.loading = true; this.error = ''; this.retryDisabled = false;
    this.subscription = this.service.resolveLookupSource(owner, binding).subscribe({
      next: value => {
        if (!this.accept(request, baseline)) return;
        this.loading = false;
        if (!isWorkflowLookupSourceRead(value, owner, binding)) { this.error = 'The source response does not match this saved draft. Close and reopen the selector.'; return; }
        const reason = workflowLookupSourceReason(value.source) || this.choiceReason(value.source);
        if (reason) { this.sources = this.sources.map(row => workflowLookupSourceBindingKey(row.binding) === id ? value.source : row); this.error = reason; return; }
        this.pendingSelection = { source: structuredClone(value.source), owner, context }; this.internalOpen = false;
      },
      error: error => {
        if (!this.accept(request, baseline)) return;
        if (this.requiresRefresh(error)) this.invalidateOwner(error);
        else { this.loading = false; this.error = this.readError(error); }
      },
    });
  }
  reviewDependency(event: { id: string; actionId: string }): void {
    const source = this.source(event.id);
    if (this.retryDisabled || !this.internalOpen || !this.current(this.baseline) || this.loading || !source) return;
    const dependency = this.dependency(source);
    if (!dependency || dependency.id !== event.actionId) return;
    this.pendingDependency = { id: dependency.id, baseline: this.baseline }; this.internalOpen = false;
  }
  private begin(): void {
    this.stopRequest(); this.stopSavedRead(); this.baseline = this.currentKey(); this.pendingSelection = null; this.pendingDependency = null;
    this.query = { search: '', pageNumber: 1, pageSize: 25 }; this.sources = []; this.totalCount = 0; this.error = ''; this.retryDisabled = false;
    this.savedSource = null; this.savedSourceError = ''; this.internalOpen = !!this.owner();
    if (this.internalOpen) { this.load(); this.readSavedSource(); }
  }
  private readSavedSource(): void {
    const baseline = this.baseline;
    if (!this.savedBinding || !this.current(baseline) || !this.internalOpen) return;
    const owner = structuredClone(this.owner()!), binding = structuredClone(this.savedBinding), request = ++this.savedRequest;
    this.savedSourceLoading = true;
    this.savedRead = this.service.resolveLookupSource(owner, binding).subscribe({
      next: value => {
        if (request !== this.savedRequest || !this.internalOpen || !this.current(baseline)) return;
        this.savedSourceLoading = false;
        if (!isWorkflowLookupSourceRead(value, owner, binding)) { this.savedSourceError = 'The saved source response does not match this draft. Its binding is preserved.'; return; }
        this.savedSource = structuredClone(value.source); this.savedSourceError = workflowLookupSourceReason(value.source);
      },
      error: error => {
        if (request !== this.savedRequest || !this.internalOpen || !this.current(baseline)) return;
        this.savedSourceLoading = false; this.savedSource = null;
        if (this.requiresRefresh(error)) {
          this.invalidateOwner(error);
        } else if ((error as { status?: number })?.status === 404) {
          this.savedSourceError = 'The saved lookup source is unavailable. Its binding is preserved.';
        } else {
          this.savedSourceError = ''; this.error = 'The saved lookup source could not be checked. Retry to refresh sources.';
        }
      },
    });
  }
  private load(): void {
    const baseline = this.baseline;
    if (!this.current(baseline) || !this.internalOpen) return;
    const owner = structuredClone(this.owner()!), query = { ...this.query };
    this.stopRequest(); const request = this.request; this.loading = true; this.error = ''; this.retryDisabled = false; this.sources = [];
    this.subscription = this.service.searchLookupSources(owner, query).subscribe({
      next: value => {
        if (!this.accept(request, baseline)) return;
        this.loading = false;
        if (!isWorkflowLookupSourcePage(value, owner, query)) { this.error = 'The source response does not match this saved draft. Close and reopen the selector.'; this.totalCount = 0; return; }
        this.sources = structuredClone(value.items); this.totalCount = value.totalCount;
      },
      error: error => {
        if (!this.accept(request, baseline)) return;
        if (this.requiresRefresh(error)) this.invalidateOwner(error);
        else { this.loading = false; this.error = this.readError(error); }
      },
    });
  }
  private accept(request: number, baseline: string): boolean { return request === this.request && this.internalOpen && this.current(baseline); }
  private current(baseline: string): boolean { return this.open && this.contextCurrent(baseline); }
  private contextCurrent(baseline: string): boolean { return !this.destroyed && this.active && !this.disabled && !!this.owner() && baseline === this.currentKey(); }
  private currentKey(): string { return JSON.stringify([workflowLookupSourceOwnerKey(this.owner()), this.context(), this.active, this.disabled,
    this.savedBinding && workflowLookupSourceBindingKey(this.savedBinding)]); }
  private stopRequest(): void { this.request++; this.subscription?.unsubscribe(); this.subscription = null; if (this.debounce) clearTimeout(this.debounce); this.debounce = null; this.loading = false; }
  private stopSavedRead(): void { this.savedRequest++; this.savedRead?.unsubscribe(); this.savedRead = null; this.savedSourceLoading = false; }
  private invalidateOwner(error: unknown): void {
    // Both reads belong to one saved owner. A denial/stale result from either
    // lane fences all queued work before another response can repopulate it.
    this.stopRequest(); this.stopSavedRead(); this.retryDisabled = true;
    this.sources = []; this.savedSource = null; this.totalCount = 0; this.pendingSelection = null; this.pendingDependency = null;
    this.savedSourceError = ''; this.error = this.readError(error);
  }
  private cancel(): void { this.stopRequest(); this.stopSavedRead(); this.pendingSelection = null; this.pendingDependency = null; this.internalOpen = false; }
  private readError(error: unknown): string {
    const status = (error as { status?: number })?.status;
    return status === 401 || status === 403 ? 'Lookup sources are unavailable with the current authoring permission. Close this selector and refresh current state.'
      : status === 409 || status === 412 ? 'The saved draft changed. Close this selector and refresh current state before choosing a source.'
        : 'Lookup sources could not be read. Retry to load the current sources.';
  }
  private requiresRefresh(error: unknown): boolean { return [401, 403, 409, 412].includes((error as { status?: number })?.status ?? 0); }
}
